import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import RCLayout from "@/pages/rc/RCLayout";
import { SECTIONS } from "@/pages/rc/eligibilityReference";
import { Search, ShieldCheck, Ban, BookOpen } from "lucide-react";

const ACCENTS = {
  emerald: { card: "border-emerald-200 bg-emerald-50/40", dot: "bg-emerald-500", icon: "text-emerald-600", head: "text-emerald-900" },
  rose: { card: "border-rose-200 bg-rose-50/40", dot: "bg-rose-500", icon: "text-rose-600", head: "text-rose-900" },
  blue: { card: "border-stone-200 bg-white", dot: "bg-blue-500", icon: "text-blue-600", head: "text-stone-900" },
};

const HeadIcon = ({ accent }) =>
  accent === "emerald" ? <ShieldCheck className="w-5 h-5" strokeWidth={1.5} />
    : accent === "rose" ? <Ban className="w-5 h-5" strokeWidth={1.5} />
    : <BookOpen className="w-5 h-5" strokeWidth={1.5} />;

export default function RCEligibility() {
  const [q, setQ] = useState("");

  const sections = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return SECTIONS;
    return SECTIONS
      .map((s) => ({
        ...s,
        items: s.items.filter(
          (it) => it.title.toLowerCase().includes(term) || (it.desc || "").toLowerCase().includes(term)
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [q]);

  return (
    <RCLayout>
      <div data-testid="eligibility-page">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-stone-900">Ammissibilità & Glossario</h1>
          <p className="text-stone-500 text-sm mt-1">
            Chi può essere assicurato con RC Economia Solidale, classificazioni del Glossario ed esclusioni (Art. 34).
          </p>
        </div>

        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" strokeWidth={1.5} />
          <Input
            data-testid="eligibility-search"
            placeholder="Cerca tipologia di ente (es. cooperativa, sportiva, fondazione)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 rounded-sm"
          />
        </div>

        <div className="space-y-6">
          {sections.map((s) => {
            const a = ACCENTS[s.accent] || ACCENTS.blue;
            return (
              <section key={s.key} data-testid={`elig-section-${s.key}`} className={`border rounded-sm shadow-sm p-6 ${a.card}`}>
                <div className="flex items-start gap-3 mb-1">
                  <span className={a.icon}><HeadIcon accent={s.accent} /></span>
                  <div>
                    <h2 className={`font-display text-lg font-semibold ${a.head}`}>{s.label}</h2>
                    <p className="text-stone-500 text-sm">{s.note}</p>
                  </div>
                  <span className="ml-auto text-xs font-mono text-stone-400">{s.items.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 mt-4">
                  {s.items.map((it) => (
                    <div key={it.title} className="flex gap-2.5">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${a.dot}`} />
                      <div>
                        <div className="text-sm font-medium text-stone-800">{it.title}</div>
                        {it.desc && <div className="text-xs text-stone-500 mt-0.5 leading-relaxed">{it.desc}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
          {sections.length === 0 && (
            <p data-testid="elig-empty" className="text-stone-400 text-sm">Nessun risultato per “{q}”.</p>
          )}
        </div>
      </div>
    </RCLayout>
  );
}
